import React, { useState, useEffect } from 'react';
import RetailerSidebar from './RetailerSidebar';
import { uploadCompanyLogo, toAssetUrl } from '../../services/api';
import room12 from "../../assets/rooms/room1.jpg";

export default function ImageSettings() {
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoPosition, setLogoPosition] = useState('bottom-right');
  const [logoSize, setLogoSize] = useState('medium');
  const [customText, setCustomText] = useState('');
  const [textPosition, setTextPosition] = useState('bottom-left');
  const [fontSize, setFontSize] = useState('16');
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // Demo room image
  const demoRoomImage = room12;

  useEffect(() => {
    // Load existing retailer data if available
    const retailerData = localStorage.getItem('retailer_data');
    if (retailerData) {
      const parsed = JSON.parse(retailerData);
      if (parsed.company_logo) {
        setLogoPreview(toAssetUrl(parsed.company_logo));
      }
    }
  }, []);

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpload = async () => {
    if (!logoFile) {
      setErrorMessage('Please select a logo image');
      return;
    }

    setLoading(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      const token = localStorage.getItem('retailer_token');
      const response = await uploadCompanyLogo(logoFile);
      
      if (response.success) {
        setSuccessMessage('Company logo uploaded successfully!');
        // Update retailer data in localStorage
        if (response.retailer) {
          localStorage.setItem('retailer_data', JSON.stringify(response.retailer));
        }
        setLogoFile(null);
      } else {
        setErrorMessage('Failed to upload logo');
      }
    } catch (error) {
      setErrorMessage(error.message || 'Failed to upload logo');
    } finally {
      setLoading(false);
    }
  };

  const getLogoStyle = () => {
    const positions = {
      'top-left': { top: '20px', left: '20px', bottom: 'auto', right: 'auto' },
      'top-right': { top: '20px', right: '20px', bottom: 'auto', left: 'auto' },
      'bottom-left': { bottom: '20px', left: '20px', top: 'auto', right: 'auto' },
      'bottom-right': { bottom: '20px', right: '20px', top: 'auto', left: 'auto' },
      'center': { top: '50%', left: '50%', transform: 'translate(-50%, -50%)', bottom: 'auto', right: 'auto' },
    };

    const sizes = {
      'small': { width: '80px', height: 'auto' },
      'medium': { width: '120px', height: 'auto' },
      'large': { width: '180px', height: 'auto' },
    };

    return {
      ...positions[logoPosition],
      ...sizes[logoSize],
      position: 'absolute',
      zIndex: 10,
    };
  };

  const getTextStyle = () => {
    const positions = {
      'top-left': { top: '20px', left: '20px', bottom: 'auto', right: 'auto' },
      'top-right': { top: '20px', right: '20px', bottom: 'auto', left: 'auto' },
      'bottom-left': { bottom: '20px', left: '20px', top: 'auto', right: 'auto' },
      'bottom-right': { bottom: '20px', right: '20px', top: 'auto', left: 'auto' },
      'center': { top: '50%', left: '50%', transform: 'translate(-50%, -50%)', bottom: 'auto', right: 'auto' },
    };

    return {
      ...positions[textPosition],
      position: 'absolute',
      fontSize: `${fontSize}px`,
      fontWeight: 'bold',
      color: 'white',
      textShadow: '2px 2px 4px rgba(0,0,0,0.7)',
      zIndex: 10,
    };
  };

  return (
    <div className="flex">
      <RetailerSidebar />
      <div className="flex-1 ml-64 p-8">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">Image & Catalog Settings</h1>

          {successMessage && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg text-green-800">
              {successMessage}
            </div>
          )}

          {errorMessage && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800">
              {errorMessage}
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Settings Panel */}
            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Your Profile Logo</h2>
              
              <div className="space-y-6">
                {/* Logo Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Choose Logo Image
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoChange}
                    className="block w-full text-sm text-gray-500
                      file:mr-4 file:py-2 file:px-4
                      file:rounded-lg file:border-0
                      file:text-sm file:font-medium
                      file:bg-accent file:text-white
                      hover:file:bg-accent2
                      cursor-pointer"
                  />
                  <p className="mt-2 text-xs text-gray-500">
                    Supported formats: JPG, PNG, WebP, SVG (max 5MB)
                  </p>
                </div>

                {/* Logo Position */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Logo Position
                  </label>
                  <select
                    value={logoPosition}
                    onChange={(e) => setLogoPosition(e.target.value)}
                    className="block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-accent"
                  >
                    <option value="top-left">Top Left</option>
                    <option value="top-right">Top Right</option>
                    <option value="bottom-left">Bottom Left</option>
                    <option value="bottom-right">Bottom Right</option>
                    <option value="center">Center</option>
                  </select>
                </div>

                {/* Logo Size */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Logo Size
                  </label>
                  <select
                    value={logoSize}
                    onChange={(e) => setLogoSize(e.target.value)}
                    className="block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-accent"
                  >
                    <option value="small">Small</option>
                    <option value="medium">Medium</option>
                    <option value="large">Large</option>
                  </select>
                </div>

                {/* Custom Text */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Enter Custom Text
                  </label>
                  <input
                    type="text"
                    value={customText}
                    onChange={(e) => setCustomText(e.target.value)}
                    placeholder="YOUR COMPANY LOGO HERE"
                    className="block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-accent"
                  />
                </div>

                {/* Text Position */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Text Position
                  </label>
                  <select
                    value={textPosition}
                    onChange={(e) => setTextPosition(e.target.value)}
                    className="block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-accent"
                  >
                    <option value="top-left">Top Left</option>
                    <option value="top-right">Top Right</option>
                    <option value="bottom-left">Bottom Left</option>
                    <option value="bottom-right">Bottom Right</option>
                    <option value="center">Center</option>
                  </select>
                </div>

                {/* Font Size */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Font Size (px)
                  </label>
                  <input
                    type="number"
                    value={fontSize}
                    onChange={(e) => setFontSize(e.target.value)}
                    min="10"
                    max="72"
                    className="block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-accent"
                  />
                </div>

                {/* Upload Button */}
                <button
                  onClick={handleUpload}
                  disabled={loading || !logoFile}
                  className="w-full px-6 py-3 bg-accent hover:bg-accent2 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Uploading...' : 'Upload Logo'}
                </button>
              </div>
            </div>

            {/* Preview Panel */}
            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Image Preview</h2>
              
              <div className="relative rounded-lg overflow-hidden bg-gray-100">
                <img
                  src={demoRoomImage}
                  alt="Demo Room"
                  className="w-full h-auto"
                  onError={(e) => {
                    e.target.src = 'https://via.placeholder.com/600x400?text=Demo+Room';
                  }}
                />
                
                {logoPreview && (
                  <img
                    src={logoPreview}
                    alt="Company Logo"
                    style={getLogoStyle()}
                    className="object-contain"
                  />
                )}
                
                {customText && (
                  <div style={getTextStyle()}>
                    {customText}
                  </div>
                )}
              </div>

              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600">
                  <strong>Note:</strong> This is a preview of how your logo will appear on visualizer images.
                  The actual positioning may vary slightly depending on the room image dimensions.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
